import { MapViewerConfiguration } from "../../config/map-viewer-configuration";
import { DataTransporter } from "../data-transporter";
import { computed, makeObservable, observable } from "mobx";
import { Nullable, Optional } from "../../globals/types";
import { Constants } from "../../globals/constants";
import moment from "moment";

export class ActivityLogEntryStore {
    mapViewerConfig: MapViewerConfiguration;
    dataTransporter: DataTransporter;
    activityLogEntries: Record<string, ActivityLogEntry[]>

    constructor(mapViewerConfig: MapViewerConfiguration, dataTransporter: DataTransporter) {
        this.mapViewerConfig = mapViewerConfig;
        this.dataTransporter = dataTransporter;
        this.activityLogEntries = {};
    }

    async loadAll(): Promise<ActivityLogEntry[]> {
        const activityLogEntries = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/activity-log-entries`);
        const entryDtos: ActivityLogEntryDto[] = result.data;
        entryDtos.forEach(e => activityLogEntries.push(new ActivityLogEntry(e)));
        this.activityLogEntries = ActivityLogEntryStore.createRecord(activityLogEntries);
    }

    async load(options?: FindFilter): Promise<ActivityLogEntry[]> {
        const activityLogEntries = [];
        let queryString = "";
        let first = true;

        if (options?.tableName !== undefined) {
            let prefix: string;

            if (first) {
                prefix = "?";
                first = false;
            } else {
                prefix = "&";
            }

            queryString += `${prefix}tableName=${options.tableName}`;
        }

        if (options?.rowId !== undefined) {
            let prefix: string;

            if (first) {
                prefix = "?";
                first = false;
            } else {
                prefix = "&";
            }

            queryString += `${prefix}rowId=${options.rowId}`;
        }

        if (options?.action !== undefined) {
            let prefix: string;

            if (first) {
                prefix = "?";
            } else {
                prefix = "&";
            }

            queryString += `${prefix}action=${options.action}`;
        }

        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/activity-log-entries${queryString}`);
        const activityLogEntryDtos: ActivityLogEntryDto[] = result.data;
        activityLogEntryDtos.forEach(e => activityLogEntries.push(new ActivityLogEntry(e)))
        const newActivityLogEntries = ActivityLogEntryStore.createRecord(activityLogEntries);
        this.activityLogEntries = { ...this.activityLogEntries, ...newActivityLogEntries };
    }

    private static createRecord(activityLogEntries: ActivityLogEntry[]): Record<string, ActivityLogEntry[]> {
        return activityLogEntries.reduce((result, entry) => {
            if (!(entry.tableName in result)) {
                result[entry.tableName] = [];
            }

            result[entry.tableName].push(entry);
            return result;
        }, {});
    }
}

export class ActivityLogEntry {
    activityLogEntryDto: ActivityLogEntryDto;

    constructor(activityLogEntryDto: ActivityLogEntryDto) {
        this.activityLogEntryDto = activityLogEntryDto;

        makeObservable(this, {
            activityLogEntryDto: observable,
            id: computed,
            tableName: computed,
            rowId: computed,
            action: computed,
            userId: computed,
            date: computed,
            actionData: computed,
        })
    }

    get id(): number {
        return this.activityLogEntryDto.id;
    }

    get tableName(): string {
        return this.activityLogEntryDto.tableName;
    }

    get rowId(): number {
        return this.activityLogEntryDto.rowId;
    }

    get action(): string {
        return this.activityLogEntryDto.action;
    }

    get userId(): number {
        return this.activityLogEntryDto.userId;
    }

    get timestamp(): number {
        return this.activityLogEntryDto.timestamp;
    }

    get date(): string {
        return moment(this.activityLogEntryDto.timestamp).format(Constants.dateFormat);
    }

    get actionData(): Optional<string> {
        return this.activityLogEntryDto.actionData ?? undefined;
    }
}

export type ActivityLogEntryDto = {
    id: number;
    tableName: string;
    rowId: number;
    action: string;
    userId: number;
    timestamp: number;
    actionData: Nullable<string>;
};

type FindFilter = {
    tableName?: string;
    rowId?: number;
    action?: string;
};
